import {sendEvent} from '@github-ui/hydro-analytics'

import type {CopilotChatManager} from './copilot-chat-manager'
import type {LoadingReference, ThreadScopedFileReference} from './copilot-chat-types'

const TEXT_ATTACHMENT_SIZE_LIMIT = 500 * 1024 // 500KB

// Fake mime type that indicates the source of the text is the clipboard
export const CLIPBOARD_MIME_TYPE = 'application/x.clipboard'

// Matches any `text/*` mime type
const TEXT_MIME_TYPE_REGEX = /^text\/([^/\s]+)$/i

const ALLOWED_MIME_TYPES = new Set([
  CLIPBOARD_MIME_TYPE,
  'application/json',
  'application/xml',
  'application/javascript',
  '', // empty string represents unknown type that the OS doesn't know how to handle. This is not uncommon; for example `.md` files have this by default on MacOS
])

// This regex will allow up to 51ch to give the model a bit of room for not being very good at counting
// Sometimes the model wraps the name in backticks, so we allow for that too
const FILE_NAME_REGEX = /^\s*`?([\w-_]{3,40}\.\w{1,10})`?\s*$/

// Matches the max width of ReferenceToken in the text input
const PREFERRED_MAX_FILE_NAME_LENGTH = 20

export class CopilotTextAttacher {
  private manager: CopilotChatManager
  private existingFileNames: Set<string>

  public constructor(manager: CopilotChatManager, existingFileNames: Set<string> = new Set()) {
    this.manager = manager
    this.existingFileNames = existingFileNames
  }

  public static isAttachable(file: File): boolean {
    return (
      (TEXT_MIME_TYPE_REGEX.test(file.type) || ALLOWED_MIME_TYPES.has(file.type)) &&
      file.size <= CopilotTextAttacher.getAttachmentSizeLimit()
    )
  }

  public static getAttachmentSizeLimit(): number {
    return TEXT_ATTACHMENT_SIZE_LIMIT
  }

  private makePlaceholderReference(file: File): LoadingReference {
    return {
      type: 'loading',
      title: file.name,
      id: crypto.randomUUID(),
      isClientOnly: true,
    }
  }

  private async makeReference(file: File): Promise<ThreadScopedFileReference> {
    const text = await file.text()

    const name = file.name || (await this.generateFileName(text, file.type))
    const extension = name.split('.').pop()
    sendEvent('copilot.attach_pasted_file', {extension, characters: text.length})

    return {
      type: 'thread-scoped-file',
      name,
      text,
      language: '',
    }
  }

  public async addAttachment(file: File) {
    if (!CopilotTextAttacher.isAttachable(file)) return

    const placeholder = this.makePlaceholderReference(file)
    this.manager.addReference(placeholder, 'text-attacher')

    const reference = await this.makeReference(file)
    this.manager.replaceReference(placeholder, reference)
  }

  private async generateFileName(text: string, type: string): Promise<string> {
    const namingConventionPrompt =
      this.existingFileNames.size > 0
        ? `\n\nFollow the naming formats used by these existing files: ${Array.from(this.existingFileNames)
            .slice(0, 5)
            .join(', ')}`
        : ''

    const nameResponse = await this.manager.service.getSimpleCompletion(
      `Determine a suitable name for this file. Respond with only the file name, and try to limit the name to ${PREFERRED_MAX_FILE_NAME_LENGTH} characters or less.${namingConventionPrompt}

File contents:
\`\`\`
${text}
\`\`\`
`,
    )
    const name = nameResponse.ok ? FILE_NAME_REGEX.exec(nameResponse.payload)?.[1] : null

    if (name) {
      sendEvent('copilot.generate_pasted_file_name.success')
    } else {
      sendEvent('copilot.generate_pasted_file_name.failure')
    }

    return name ?? this.defaultFileName(type)
  }

  private defaultFileName(mimeType: string) {
    switch (mimeType) {
      case CLIPBOARD_MIME_TYPE:
        return 'pasted.txt'
      default:
        return 'untitled.txt'
    }
  }
}
